import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AuthenticationService,AppConfigService } from '@alfresco/adf-core';


import {
  CommentRequest,
  CompleteTaskRequest,
  ForwardTaskRequest,
  FormSchema,
  InstanceDetail,
  InstanceSummary,
  PageResponse,
  ReassignTaskRequest,
  RuntimeMenuNode,
  StartInstanceRequest,
  TaskComment,
  TaskForm,
  TaskSummary,
  MyTasksColumn
} from './wf-runtime-types';

/**
 * Resource API services for the wf-runtime module — the user-facing
 * inbox, task detail page, and runtime sidebar.
 *
 * <h2>Auth pattern</h2>
 *
 * <p>This Angular app has no global HTTP interceptor — every service
 * manually injects {@link AuthenticationService} from
 * {@code @alfresco/adf-core} and adds the Bearer token to every request.
 * Same convention as {@code wf-admin/services/wf-api.services.ts},
 * {@code MenuService}, {@code ContractInvoiceService}, etc.
 *
 * <h2>Phase 7 scope</h2>
 *
 * <p>Round 1 ships these clients ready-to-use; the components that
 * consume them (TaskListComponent, TaskDetailComponent, runtime sidebar)
 * arrive in Round 2 and beyond. Building the API layer first means the
 * UI work in later rounds is purely template + binding code.
 */

@Injectable({ providedIn: 'root' })
export class WfApiConfig {
  readonly base: string;
  constructor(appConfig: AppConfigService) {
    this.base = `${appConfig.get<string>('skyplus.apiUrl', '/bpm/api')}/wf`;
  }
}

/** Authorization-only headers (works for GET + DELETE + multipart POST). */
function authHeaders(auth: AuthenticationService): HttpHeaders {
  const token = auth.getToken();
  return token
    ? new HttpHeaders({ 'Authorization': `Bearer ${token}` })
    : new HttpHeaders();
}

/** Auth + JSON Content-Type — for POST/PUT with a JSON body. */
function jsonHeaders(auth: AuthenticationService): HttpHeaders {
  return authHeaders(auth).set('Content-Type', 'application/json');
}

// ─── Runtime Menu (Phase 7) ─────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class WfRuntimeMenuApi {
  constructor(
    private http: HttpClient,
    private auth: AuthenticationService,
    private cfg: WfApiConfig
  ) {}

  /**
   * Fetch the 3-level runtime menu tree (Category → Workflow → Step) for
   * the current user. The server has already filtered children by the
   * caller's roles — the client renders the tree verbatim.
   *
   * <p>Drives the user-facing /workflow sidebar that arrives in Round 2.
   */
  tree(): Observable<RuntimeMenuNode[]> {
    return this.http.get<RuntimeMenuNode[]>(
      `${this.cfg.base}/menu/runtime`,
      { headers: authHeaders(this.auth) }
    );
  }
}

// ─── Tasks ──────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class WfTaskApi {
  constructor(
    private http: HttpClient,
    private auth: AuthenticationService,
    private cfg: WfApiConfig
  ) {}

  // ── Inbox listings ────────────────────────────────────────────────────────

  /**
   * Tasks currently assigned to the calling user across every running
   * workflow. Backend: {@code GET /api/wf/tasks/my}.
   *
   * <p>Pagination uses Spring Data's {@code page} / {@code size} query
   * params; the response is a {@link PageResponse} — drill into
   * {@code .content[]} to read rows.
   *
   * <p>Phase 7 — optional {@code wfKey}/{@code taskKey} pin the listing
   * to a specific workflow step (drives the runtime right pane). Both
   * undefined = legacy cross-workflow inbox.
   */
  myTasks(page: number = 0,
          size: number = 20,
          wfKey?: string,
          taskKey?: string): Observable<PageResponse<TaskSummary>> {
    let params = new HttpParams()
      .set('page', String(page))
      .set('size', String(size));
    if (wfKey)   params = params.set('wfKey',   wfKey);
    if (taskKey) params = params.set('taskKey', taskKey);
    return this.http.get<PageResponse<TaskSummary>>(
      `${this.cfg.base}/tasks/my`,
      { headers: authHeaders(this.auth), params }
    );
  }

  /**
   * Tasks unassigned but claimable by the calling user's groups.
   * Backend: {@code GET /api/wf/tasks/claimable?group=x&group=y}.
   *
   * <p>The kernel's {@code GroupResolver} bean isn't built yet (per the
   * Phase 4 notes), so callers must pass the user's group keys
   * explicitly — typically resolved client-side from the JWT roles.
   *
   * <p>Phase 7 — accepts the same optional {@code wfKey}/{@code taskKey}
   * filters as {@link #myTasks}.
   */
  claimable(groups: string[],
            page: number = 0,
            size: number = 20,
            wfKey?: string,
            taskKey?: string): Observable<PageResponse<TaskSummary>> {
    let params = new HttpParams()
      .set('page', String(page))
      .set('size', String(size));
    for (const g of groups) {
      params = params.append('group', g);
    }
    if (wfKey)   params = params.set('wfKey',   wfKey);
    if (taskKey) params = params.set('taskKey', taskKey);
    return this.http.get<PageResponse<TaskSummary>>(
      `${this.cfg.base}/tasks/claimable`,
      { headers: authHeaders(this.auth), params }
    );
  }

  /** The configured My Tasks column set (global, cross-workflow). */
  getMyTasksColumns(): Observable<MyTasksColumn[]> {
    return this.http.get<MyTasksColumn[]>(
      `${this.cfg.base}/tasks/my/columns`,
      { headers: authHeaders(this.auth) }
    );
  }

  /** Replace the global My Tasks column set. */
  saveMyTasksColumns(columns: MyTasksColumn[]): Observable<MyTasksColumn[]> {
    return this.http.put<MyTasksColumn[]>(
      `${this.cfg.base}/tasks/my/columns`,
      columns,
      { headers: authHeaders(this.auth) }
    );
  }

  /**
   * Phase 7 — every active task at a step, irrespective of assignee.
   * Backend: {@code GET /api/wf/tasks/at-step?wfKey=&taskKey=}. Used by
   * read-only viewers (audit) and admins; the backend authorises via
   * {@code WfRuntimeMenuService.resolveAccess} — anything other than
   * {@code edit} or {@code read} is rejected with 403.
   */
  atStep(wfKey: string,
         taskKey: string,
         page: number = 0,
         size: number = 20): Observable<PageResponse<TaskSummary>> {
    const params = new HttpParams()
      .set('wfKey', wfKey)
      .set('taskKey', taskKey)
      .set('page', String(page))
      .set('size', String(size));
    return this.http.get<PageResponse<TaskSummary>>(
      `${this.cfg.base}/tasks/at-step`,
      { headers: authHeaders(this.auth), params }
    );
  }

  // ── Task detail ───────────────────────────────────────────────────────────

  /**
   * Full task detail with form schema + data + visibility maps merged in.
   * Backend: {@code GET /api/wf/tasks/{taskId}}. The response is ready to
   * hand to a Form.io renderer without further transformation.
   */
  get(taskId: string): Observable<TaskForm> {
    return this.http.get<TaskForm>(
      `${this.cfg.base}/tasks/${encodeURIComponent(taskId)}`,
      { headers: authHeaders(this.auth) }
    );
  }

  // ── Lifecycle actions ─────────────────────────────────────────────────────

  /**
   * Complete the task with a chosen outcome. The kernel writes the form
   * data into the instance under the
   * {@code form_<formKey>_<field>} prefix and dispatches the next step
   * via the BPMN gateway.
   *
   * <p>Backend: {@code POST /api/wf/tasks/{taskId}/complete}.
   */
   complete(taskId: string, body: CompleteTaskRequest): Observable<void> {
    return this.http.post<void>(
      `${this.cfg.base}/tasks/${encodeURIComponent(taskId)}/complete`,
      body,
      { headers: jsonHeaders(this.auth) }
    );
  }
 
  /**
   * H.3-b Item 3 — Save the form's current values as a draft. Merges
   * into the instance's data_json without completing the Flowable task,
   * so the user can return later and see their in-progress entries.
   *
   * <p>Backend: {@code PATCH /api/wf/tasks/{taskId}/draft}.
   */
  saveDraft(taskId: string, data: Record<string, unknown>): Observable<void> {
    return this.http.patch<void>(
      `${this.cfg.base}/tasks/${encodeURIComponent(taskId)}/draft`,
      { data },
      { headers: jsonHeaders(this.auth) }
    );
  }

  /**
   * Forward the task to another user or group without completing it.
   * Backend: {@code POST /api/wf/tasks/{taskId}/forward}.
   */
  forward(taskId: string, body: ForwardTaskRequest): Observable<void> {
    return this.http.post<void>(
      `${this.cfg.base}/tasks/${encodeURIComponent(taskId)}/forward`,
      body,
      { headers: jsonHeaders(this.auth) }
    );
  }

  /**
   * Bring-up / defer: park this task until {@code until}; the scheduler returns it to the caller
   * on that date. Backend: {@code POST /api/wf/tasks/{taskId}/bring-up}.
   */
  bringUp(taskId: string, until: string, note?: string, outcome?: string): Observable<void> {
    return this.http.post<void>(
      `${this.cfg.base}/tasks/${encodeURIComponent(taskId)}/bring-up`,
      { until, note, outcome },
      { headers: jsonHeaders(this.auth) }
    );
  }

  /** Wake a parked task now (early bring-up). Backend: {@code POST /tasks/{taskId}/bring-up-now}. */
  bringUpNow(taskId: string): Observable<void> {
    return this.http.post<void>(
      `${this.cfg.base}/tasks/${encodeURIComponent(taskId)}/bring-up-now`,
      {},
      { headers: jsonHeaders(this.auth) }
    );
  }

  /**
   * Round 6 — the FINAL approver in a sequential approval chain adds one
   * more approver instead of archiving. The backend records the actor's
   * approval, appends the user to the chain (and the source form field),
   * and reassigns the task to them.
   *
   * <p>Backend: {@code POST /api/wf/tasks/{taskId}/chain/extend}.
   */
  extendChain(taskId: string, body: {
    username: string;
    data?: Record<string, unknown>;
    comment?: string;
    ccUsernames?: string[];
    ccGroupIds?: string[];
  }): Observable<void> {
    return this.http.post<void>(
      `${this.cfg.base}/tasks/${encodeURIComponent(taskId)}/chain/extend`,
      body,
      { headers: jsonHeaders(this.auth) }
    );
  }

  /**
   * Reassign the task to a different user. Backend records an audit entry.
   * Backend: {@code POST /api/wf/tasks/{taskId}/reassign}.
   */
  reassign(taskId: string, body: ReassignTaskRequest): Observable<void> {
    return this.http.post<void>(
      `${this.cfg.base}/tasks/${encodeURIComponent(taskId)}/reassign`,
      body,
      { headers: jsonHeaders(this.auth) }
    );
  }

  /** R2.4 Part B — record that this step's document was signed (after the
   *  external signer closes). Backend: POST /api/wf/tasks/{taskId}/mark-signed. */
  markSigned(taskId: string): Observable<void> {
    return this.http.post<void>(
      `${this.cfg.base}/tasks/${encodeURIComponent(taskId)}/mark-signed`,
      {},
      { headers: jsonHeaders(this.auth) }
    );
  }

  /** docx signing — prepare the document to sign (renders a PDF copy of a Word
   *  letter once) and return the node the signer should open. A PDF document is
   *  returned unchanged. Backend: POST /tasks/{taskId}/sign-doc. */
  prepareSignDoc(taskId: string): Observable<{ nodeId: string; mimeType: string; converted: boolean }> {
    return this.http.post<{ nodeId: string; mimeType: string; converted: boolean }>(
      `${this.cfg.base}/tasks/${encodeURIComponent(taskId)}/sign-doc`,
      {},
      { headers: jsonHeaders(this.auth) }
    );
  }

  /** docx signing (manual) — download the current sign document (the rendered
   *  PDF for a Word letter, else the document itself) so the user can sign it
   *  offline. Backend prepares the PDF on demand. */
  downloadSignDoc(taskId: string): Observable<Blob> {
    return this.http.get(
      `${this.cfg.base}/tasks/${encodeURIComponent(taskId)}/sign-doc/content`,
      { headers: authHeaders(this.auth), responseType: 'blob' }
    );
  }

  /** R2.4 Part B (manual) — upload a signed copy; it becomes a new version of
   *  the document and marks signed. Backend: POST /tasks/{taskId}/upload-signed. */
  uploadSigned(taskId: string, file: File): Observable<void> {
    const form = new FormData();
    form.append('file', file, file.name);
    // Auth header only — let the browser set the multipart Content-Type/boundary.
    return this.http.post<void>(
      `${this.cfg.base}/tasks/${encodeURIComponent(taskId)}/upload-signed`,
      form,
      { headers: authHeaders(this.auth) }
    );
  }

  /**
   * Claim a task that is currently unassigned but claimable by one of the
   * caller's groups. Backend: {@code POST /api/wf/tasks/{taskId}/claim}.
   * Idempotent — claiming an already-claimed task by the same user is a no-op.
   */
  claim(taskId: string): Observable<void> {
    return this.http.post<void>(
      `${this.cfg.base}/tasks/${encodeURIComponent(taskId)}/claim`,
      null,
      { headers: jsonHeaders(this.auth) }
    );
  }

  /** Return a claimed group task to its pool (only the current holder). Backend:
   *  {@code POST /api/wf/tasks/{taskId}/unclaim}. */
  unclaim(taskId: string): Observable<void> {
    return this.http.post<void>(
      `${this.cfg.base}/tasks/${encodeURIComponent(taskId)}/unclaim`,
      null,
      { headers: jsonHeaders(this.auth) }
    );
  }

  // ── Comments ──────────────────────────────────────────────────────────────

  /**
   * Add a comment to the task. Backend:
   * {@code POST /api/wf/tasks/{taskId}/comments}.
   */
  comment(taskId: string, body: CommentRequest): Observable<TaskComment> {
    return this.http.post<TaskComment>(
      `${this.cfg.base}/tasks/${encodeURIComponent(taskId)}/comments`,
      body,
      { headers: jsonHeaders(this.auth) }
    );
  }
}

// ─── Phase 7 Round 4 — instance + form schema services ──────────────────────

/**
 * Workflow instance API — used by the runtime detail page to fetch the
 * audit log ("signatures"), merged form data, and comments for the
 * current task's parent instance. Also wraps the start-instance endpoint
 * for the new start-form renderer.
 */
@Injectable({ providedIn: 'root' })
export class WfInstanceApi {
  constructor(
    private http: HttpClient,
    private auth: AuthenticationService,
    private cfg: WfApiConfig
  ) {}

  /**
   * Full instance detail — summary, merged data, audit history, comments,
   * attachments, participants. Backend: {@code GET /api/wf/instances/{id}}.
   *
   * <p>The runtime detail page calls this in parallel with
   * {@code WfTaskApi.get()} to render the audit panel + context panel
   * + comments thread alongside the task form.
   */
  get(id: number): Observable<InstanceDetail> {
    return this.http.get<InstanceDetail>(
      `${this.cfg.base}/instances/${id}`,
      { headers: authHeaders(this.auth) }
    );
  }

  /**
   * Start a new workflow instance. Backend:
   * {@code POST /api/wf/{wfKey}/instances}. Kernel creates the
   * {@code wf_instance} row, deploys the Flowable process, runs through
   * the start event, and parks at the first user task (which the user
   * then opens via the runtime detail page to advance further).
   */
  start(wfKey: string, request: StartInstanceRequest): Observable<InstanceSummary> {
    return this.http.post<InstanceSummary>(
      `${this.cfg.base}/${encodeURIComponent(wfKey)}/instances`,
      request,
      { headers: jsonHeaders(this.auth) }
    );
  }

  /**
   * Cancel an in-flight instance. Backend:
   * {@code POST /api/wf/instances/{id}/cancel}. Optional reason is
   * captured in the audit log.
   */
  cancel(id: number, reason?: string): Observable<void> {
    let params = new HttpParams();
    if (reason) params = params.set('reason', reason);
    return this.http.post<void>(
      `${this.cfg.base}/instances/${id}/cancel`,
      null,
      { headers: jsonHeaders(this.auth), params }
    );
  }
}

/**
 * Form schema API — used by the start-form renderer to fetch a workflow's
 * first userTask form schema by formKey, before any instance exists.
 * Same endpoint that the wf-admin form designer reads for editing.
 */
@Injectable({ providedIn: 'root' })
export class WfFormSchemaApi {
  constructor(
    private http: HttpClient,
    private auth: AuthenticationService,
    private cfg: WfApiConfig
  ) {}

  /**
   * Fetch a form schema by its stable formKey. Backend:
   * {@code GET /api/wf/forms/schemas/{formKey}}. Returns the latest
   * active version of the schema.
   */
  get(formKey: string): Observable<FormSchema> {
    return this.http.get<FormSchema>(
      `${this.cfg.base}/forms/schemas/${encodeURIComponent(formKey)}`,
      { headers: authHeaders(this.auth) }
    );
  }
}